import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ProductCard from './components/ProductCard';
import { products, categories, conditions } from '../../mocks/products';

export default function Products() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialCategory = params.get('category') || 'All';

  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 30]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const cat = p.get('category');
    if (cat) setActiveCategory(cat);
  }, [location.search]);

  const toggleCondition = (c: string) => {
    setSelectedConditions(prev =>
      prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]
    );
  };

  const filtered = products.filter(p => {
    const catMatch = activeCategory === 'All' || p.category === activeCategory;
    const condMatch = selectedConditions.length === 0 || selectedConditions.includes(p.condition);
    const priceMatch = p.price >= priceRange[0] && p.price <= priceRange[1];
    const searchMatch = search === '' || p.name.toLowerCase().includes(search.toLowerCase());
    return catMatch && condMatch && priceMatch && searchMatch;
  });

  return (
    <div style={{ fontFamily: "'Nunito', sans-serif" }}>
      <Navbar />

      <section className="pt-28 pb-12 relative" style={{ backgroundColor: '#F5F2FD' }}>
        <div className="max-w-7xl mx-auto px-6">
          <span className="text-brand-purple text-xs font-600 uppercase tracking-widest" style={{ fontWeight: 600 }}>Our store</span>
          <h1 className="text-4xl font-700 text-gray-800 mt-1 mb-3" style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 700 }}>
            All Products
          </h1>
          <p className="text-gray-500 text-sm">Second-hand clothing and used toys for kids ages 1 to 12</p>

          <div className="mt-6 relative max-w-md">
            <div className="flex items-center justify-center w-10 h-10 absolute left-3 top-1/2 -translate-y-1/2">
              <i className="ri-search-line text-gray-400 text-base"></i>
            </div>
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-brand-yellow"
            />
          </div>
        </div>
      </section>

      <section className="bg-white border-b border-gray-100 sticky top-[72px] z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 overflow-x-auto py-4" style={{ scrollbarWidth: 'none' }}>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`whitespace-nowrap px-5 py-2 rounded-full text-sm font-600 transition-all cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-brand-yellow text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-brand-yellow-light hover:text-brand-yellow-dark'
                }`}
                style={{ fontWeight: 600 }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-6 flex gap-8">

          <aside className="hidden lg:block w-60 flex-shrink-0">
            <div className="bg-white rounded-2xl p-6 sticky top-40">
              <h3 className="font-700 text-gray-800 mb-5" style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 700 }}>
                Filters
              </h3>

              <div className="mb-6">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-3 font-600" style={{ fontWeight: 600 }}>Condition</p>
                <div className="space-y-2">
                  {conditions.map(c => (
                    <label key={c} className="flex items-center gap-2 cursor-pointer group">
                      <div
                        className={`w-4 h-4 rounded flex items-center justify-center border transition-colors ${
                          selectedConditions.includes(c)
                            ? 'bg-brand-yellow border-brand-yellow'
                            : 'border-gray-300 group-hover:border-brand-yellow'
                        }`}
                        onClick={() => toggleCondition(c)}
                      >
                        {selectedConditions.includes(c) && (
                          <i className="ri-check-line text-white text-xs"></i>
                        )}
                      </div>
                      <span className="text-sm text-gray-600">{c}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-3 font-600" style={{ fontWeight: 600 }}>
                  Max Price: <span className="text-brand-yellow">${priceRange[1]}</span>
                </p>
                <input
                  type="range"
                  min={0}
                  max={30}
                  value={priceRange[1]}
                  onChange={e => setPriceRange([0, Number(e.target.value)])}
                  className="w-full accent-[#F5B730]"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>$0</span>
                  <span>$30</span>
                </div>
              </div>

              <button
                onClick={() => {
                  setSelectedConditions([]);
                  setPriceRange([0, 30]);
                  setActiveCategory('All');
                  setSearch('');
                }}
                className="w-full py-2.5 rounded-xl border border-gray-200 text-sm text-gray-500 hover:border-brand-yellow hover:text-brand-yellow transition-colors cursor-pointer whitespace-nowrap"
              >
                Clear filters
              </button>
            </div>
          </aside>

          <div className="flex-1">
            <p className="text-sm text-gray-400 mb-6">
              {filtered.length} product{filtered.length !== 1 ? 's' : ''} found
            </p>
            {filtered.length === 0 ? (
              <div className="text-center py-20">
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4">
                  <i className="ri-search-line text-5xl text-gray-300"></i>
                </div>
                <p className="text-gray-400 text-base">No products found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {filtered.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
